import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button, Select, Spin, Table } from 'antd';
import { MdKeyboardBackspace } from 'react-icons/md';

// Auditoria dos uploads de fotos/vídeos (só administradores). Dados de /api/upload_auditoria (WordPress).

const ESTADOS = {
    ok: 'OK',
    parcial: 'Parcial',
    falhou: 'Falhou',
    incompleto: 'Sem resumo',
};

const dataISO = (data) => {
    const d = new Date(data.getTime() - data.getTimezoneOffset() * 60000);
    return d.toISOString().slice(0, 10);
};

const tamanho = (bytes) => {
    const n = Number(bytes) || 0;
    if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
    if (n >= 1024) return `${Math.round(n / 1024)} KB`;
    return `${n} B`;
};

const duracao = (ms) => {
    const n = Number(ms) || 0;
    return n >= 1000 ? `${(n / 1000).toFixed(1)} s` : `${n} ms`;
};

const hora = (iso) => (iso ? new Date(iso).toLocaleString('pt-PT') : '—');

const isAdmin = () => {
    try {
        return (JSON.parse(localStorage.getItem('user'))?.roles || []).includes('administrator');
    } catch (error) {
        return false;
    }
};

const ChipEstado = ({ estado }) => (
    <span className={`auditoria-estado auditoria-estado-${estado}`}>{ESTADOS[estado] || estado}</span>
);

const DetalheEnvio = ({ envio }) => {
    const d = envio.detalhes || {};
    const ficheiros = d.ficheiros || [];
    const lotesCliente = d.lotes || [];

    return (
        <div className="auditoria-detalhe">
            <div className="auditoria-detalhe-grelha">
                <div><b>Início (telemóvel)</b> {hora(d.inicio)}</div>
                <div><b>Fim (telemóvel)</b> {hora(d.fim)}</div>
                <div><b>Registado no servidor</b> {envio.criado_em}</div>
                <div><b>Dispositivo</b> {d.dispositivo?.resumo || envio.dispositivo || '—'}</div>
                <div><b>Ecrã / memória</b> {d.dispositivo?.ecra || '—'}{d.dispositivo?.memoriaGb ? ` · ${d.dispositivo.memoriaGb} GB` : ''}</div>
                <div><b>Rede</b> {d.redeInicio ? `${d.redeInicio.online ? 'online' : 'offline'} ${d.redeInicio.tipo || ''} ${d.redeInicio.downlinkMbps ? `${d.redeInicio.downlinkMbps} Mbps` : ''}` : '—'}{d.redeFim && !d.redeFim.online ? ' → offline no fim' : ''}</div>
                <div><b>IP</b> {envio.ip || '—'}</div>
                <div><b>Envio</b> {envio.envio_id}</div>
            </div>
            {d.dispositivo?.userAgent && <p className="auditoria-ua">{d.dispositivo.userAgent}</p>}

            {ficheiros.length > 0 && (
                <>
                    <h4>Ficheiros escolhidos</h4>
                    <Table
                        size="small"
                        pagination={false}
                        rowKey="chave"
                        dataSource={ficheiros.map((f, i) => ({ ...f, chave: i }))}
                        scroll={{ x: true }}
                        columns={[
                            { title: 'Nome', dataIndex: 'nome' },
                            { title: 'Formato', dataIndex: 'tipo' },
                            { title: 'Original', dataIndex: 'bytesOriginal', render: tamanho },
                            { title: 'Dimensões', render: (f) => (f.largura ? `${f.largura}×${f.altura}` : '—') },
                            { title: 'Comprimido', dataIndex: 'bytesComprimido', render: (v) => (v ? tamanho(v) : '—') },
                            { title: 'Compressão', dataIndex: 'msCompressao', render: (v) => (v != null ? duracao(v) : '—') },
                            { title: 'Erro', dataIndex: 'erro', render: (v) => v || '' },
                        ]}
                    />
                </>
            )}

            <h4>Pedidos ao servidor</h4>
            {envio.lotes.length === 0 && lotesCliente.length === 0 ? (
                <p className="auditoria-vazio">Nenhum pedido chegou ao servidor.</p>
            ) : (
                <Table
                    size="small"
                    pagination={false}
                    rowKey={(l) => `${l.origem}-${l.lote}-${l.id || ''}`}
                    scroll={{ x: true }}
                    dataSource={juntarLotes(envio.lotes, lotesCliente)}
                    columns={[
                        { title: 'Lote', dataIndex: 'lote', render: (v) => v + 1 },
                        { title: 'Ficheiros', dataIndex: 'nFicheiros' },
                        { title: 'Tamanho', dataIndex: 'bytes', render: tamanho },
                        { title: 'Enviado às', dataIndex: 'inicio', render: hora },
                        { title: 'Pedido (telemóvel)', dataIndex: 'msPedido', render: (v) => (v != null ? duracao(v) : '—') },
                        { title: 'Recebido às (servidor)', dataIndex: 'recebidoEm', render: hora },
                        { title: 'Receção', dataIndex: 'msRececao', render: (v) => (v != null ? duracao(v) : '—') },
                        { title: 'WordPress', render: (l) => (l.msMedia != null ? `${duracao(l.msMedia)} + ${duracao(l.msAcf)}` : '—') },
                        { title: 'HTTP', render: (l) => (l.statusServidor && l.statusServidor !== l.status ? `${l.status || 'sem resposta'} (servidor ${l.statusServidor})` : l.status || 'sem resposta') },
                        {
                            title: 'Media',
                            dataIndex: 'mediaIds',
                            render: (ids) => (ids || []).map((id, i) => (
                                <React.Fragment key={id}>
                                    {i > 0 && ', '}
                                    {envio.media_urls?.[id]
                                        ? <a href={envio.media_urls[id]} target="_blank" rel="noopener noreferrer">{id}</a>
                                        : id}
                                </React.Fragment>
                            )),
                        },
                        { title: 'Erro', dataIndex: 'erro', render: (v) => v || '' },
                    ]}
                />
            )}
        </div>
    );
};

// Junta o que o BFF registou (servidor) com o que o telemóvel viu, por número de lote
function juntarLotes(lotesServidor, lotesCliente) {
    const linhas = new Map();
    lotesCliente.forEach((l) => {
        linhas.set(l.lote, {
            origem: 'c', lote: l.lote, nFicheiros: l.nFicheiros, bytes: l.bytes, inicio: l.inicio, msPedido: l.msPedido,
            status: l.status, mediaIds: l.mediaIds, erro: l.erro,
            recebidoEm: l.servidor?.recebidoEm, msRececao: l.servidor?.ms_rececao, msMedia: l.servidor?.ms_media, msAcf: l.servidor?.ms_acf,
        });
    });
    lotesServidor.forEach((l) => {
        const numero = Number(l.lote);
        const linha = linhas.get(numero) || { origem: 's', lote: numero, id: l.id };
        linhas.set(numero, {
            ...linha,
            nFicheiros: linha.nFicheiros ?? Number(l.n_ficheiros),
            bytes: linha.bytes ?? Number(l.bytes),
            status: linha.status !== undefined ? linha.status : Number(l.status_http),
            recebidoEm: l.detalhes?.recebidoEm || linha.recebidoEm,
            msRececao: Number(l.ms_rececao),
            msMedia: l.ms_media !== undefined && Number(l.status_http) === 200 ? Number(l.ms_media) : linha.msMedia,
            msAcf: l.ms_acf !== undefined && Number(l.status_http) === 200 ? Number(l.ms_acf) : linha.msAcf,
            mediaIds: linha.mediaIds || (l.media_ids ? l.media_ids.split(',') : []),
            // O telemóvel pode ver "Network Error" quando o servidor respondeu com erro (ou ao contrário): mostrar os dois
            erro: [linha.erro && `telemóvel: ${linha.erro}`, l.erro && `servidor: ${l.erro}`].filter(Boolean).join(' · '),
            statusServidor: Number(l.status_http),
        });
    });
    return [...linhas.values()].sort((a, b) => a.lote - b.lote);
}

const AuditoriaUploads = () => {
    const hoje = new Date();
    const [filtros, setFiltros] = useState({
        desde: dataISO(new Date(hoje.getTime() - 7 * 86400000)),
        ate: dataISO(hoje),
        user_id: '',
        estado: '',
        reserva_id: '',
        pagina: 1,
    });
    const [dados, setDados] = useState(null);
    const [aCarregar, setACarregar] = useState(false);
    const [erro, setErro] = useState('');

    const carregar = useCallback(() => {
        setACarregar(true);
        setErro('');
        const params = Object.fromEntries(Object.entries(filtros).filter(([, v]) => v !== '' && v != null));
        axios
            .get('/api/upload_auditoria', { params })
            .then((resposta) => setDados(resposta.data))
            .catch((error) => setErro(error.response?.status === 403 ? 'Só administradores podem ver a auditoria.' : 'Não foi possível carregar a auditoria.'))
            .finally(() => setACarregar(false));
    }, [filtros]);

    useEffect(() => {
        carregar();
    }, [carregar]);

    const alterar = (campo, valor) => setFiltros((f) => ({ ...f, [campo]: valor, pagina: 1 }));

    if (!isAdmin()) {
        return <div className="auditoria-uploads"><p>Não tem permissão para aceder a esta área.</p></div>;
    }

    const totais = dados?.totais || {};
    const poupanca = Number(totais.bytes_original) > 0 ? Math.round(100 - (Number(totais.bytes_enviados) / Number(totais.bytes_original)) * 100) : null;

    return (
        <div className="auditoria-uploads">
            <div className="auditoria-topo">
                <Link to="/dashboard" className="auditoria-voltar"><MdKeyboardBackspace /> Dashboard</Link>
                <h1>Auditoria de uploads</h1>
            </div>

            <div className="auditoria-filtros">
                <label>De <input type="date" className="input-geral" value={filtros.desde} onChange={(e) => alterar('desde', e.target.value)} /></label>
                <label>Até <input type="date" className="input-geral" value={filtros.ate} onChange={(e) => alterar('ate', e.target.value)} /></label>
                <label>Funcionário
                    <Select
                        value={filtros.user_id}
                        onChange={(v) => alterar('user_id', v)}
                        options={[{ value: '', label: 'Todos' }, ...(dados?.utilizadores || []).map((u) => ({ value: u.user_id, label: u.user_name }))]}
                    />
                </label>
                <label>Estado
                    <Select
                        value={filtros.estado}
                        onChange={(v) => alterar('estado', v)}
                        options={[{ value: '', label: 'Todos' }, ...Object.entries(ESTADOS).map(([value, label]) => ({ value, label }))]}
                    />
                </label>
                <label>Reserva
                    <input type="search" className="input-geral" placeholder="ID" value={filtros.reserva_id} onChange={(e) => alterar('reserva_id', e.target.value.replace(/\D/g, ''))} />
                </label>
                <Button onClick={carregar} loading={aCarregar}>Actualizar</Button>
            </div>

            {erro && <p className="auditoria-erro">{erro}</p>}

            {dados && (
                <div className="auditoria-totais">
                    <div><span>Envios</span><b>{totais.envios || 0}</b></div>
                    <div><span>Falhados</span><b className={Number(totais.falhados) ? 'auditoria-mau' : ''}>{totais.falhados || 0}</b></div>
                    <div><span>Parciais</span><b>{totais.parciais || 0}</b></div>
                    <div><span>Sem resumo</span><b>{totais.incompletos || 0}</b></div>
                    <div><span>Ficheiros</span><b>{totais.ficheiros || 0}</b></div>
                    <div><span>Original → enviado</span><b>{tamanho(totais.bytes_original)} → {tamanho(totais.bytes_enviados)}{poupanca !== null ? ` (−${poupanca}%)` : ''}</b></div>
                    <div><span>Tempo médio</span><b>{totais.ms_medio ? duracao(totais.ms_medio) : '—'}</b></div>
                </div>
            )}

            <Spin spinning={aCarregar}>
                <Table
                    rowKey="envio_id"
                    dataSource={dados?.envios || []}
                    scroll={{ x: true }}
                    expandable={{ expandedRowRender: (envio) => <DetalheEnvio envio={envio} /> }}
                    pagination={{
                        current: dados?.pagina || 1,
                        pageSize: dados?.por_pagina || 50,
                        total: Number(totais.envios) || 0,
                        showSizeChanger: false,
                        onChange: (pagina) => setFiltros((f) => ({ ...f, pagina })),
                    }}
                    columns={[
                        { title: 'Data', dataIndex: 'criado_em' },
                        { title: 'Funcionário', dataIndex: 'user_name' },
                        {
                            title: 'Reserva',
                            dataIndex: 'reserva_id',
                            render: (id, envio) => {
                                if (!Number(id)) return '—';
                                return envio.reserva_hash
                                    ? <a href={`/reserva/${envio.reserva_hash}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>#{id}</a>
                                    : `#${id}`;
                            },
                        },
                        { title: 'Tipo', dataIndex: 'tipo' },
                        { title: 'Estado', dataIndex: 'estado', render: (e) => <ChipEstado estado={e} /> },
                        { title: 'Ficheiros', render: (e) => `${e.n_enviados}/${e.n_ficheiros}` },
                        { title: 'Original → enviado', render: (e) => (Number(e.bytes_original) ? `${tamanho(e.bytes_original)} → ${tamanho(e.bytes_enviados)}` : tamanho(e.bytes_enviados)) },
                        { title: 'Duração', dataIndex: 'ms_total', render: (v) => (Number(v) ? duracao(v) : '—') },
                        { title: 'Dispositivo', dataIndex: 'dispositivo', render: (v) => <span className="auditoria-dispositivo">{v}</span> },
                        { title: 'Erro', dataIndex: 'erro', render: (v) => <span className="auditoria-erro-celula">{v}</span> },
                    ]}
                />
            </Spin>
        </div>
    );
};

export default AuditoriaUploads;
